import { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Heading from '@zenbusiness/zui/Heading';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { ProductBanners } from '#client/components/ProductBanners';
import { ProductsBreadcrumbs } from '#client/components/ProductsBreadcrumbs';
import { useProductCards, useProductCardsCache } from '#client/hooks/useProductCards';
import useScrollTo from '#client/hooks/useScrollTo';
import { DEFAULT_CATEGORY_FILTER, PRODUCTS_FILTER_PARAM_KEY } from '#client/pages/Products/constants';
import { formatSearchParams } from '#client/utils/formatString';
import {
  filterProductCardsByFeatureFlags,
  useProductCardFeatureFlags
} from '#client/utils/productCardFlags';

import { BannerConfig } from './bannerConfig';
import { ProductCards } from './components/ProductCards';
import { ProductCardsFilter } from './components/ProductCardsFilter';
import * as Styled from './Products.style';

const getBannerProps = (categoryFilter) => {
  const banner = BannerConfig[categoryFilter];
  if (Array.isArray(banner)) return banner[Math.floor(Math.random() * banner.length)];
  return banner || BannerConfig.all;
};

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { formatMessage: fm } = useIntl();

  const [searchParams] = useSearchParams();
  const categorySlug = searchParams.get(PRODUCTS_FILTER_PARAM_KEY);
  localStorage.setItem(PRODUCTS_FILTER_PARAM_KEY, categorySlug || DEFAULT_CATEGORY_FILTER);

  const cached = useProductCardsCache(categorySlug);
  const { data: products, loading: queryLoading } = useProductCards(categorySlug);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timeout;

    if (cached) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(queryLoading);
      }, 500);
    } else {
      setLoading(queryLoading);
    }

    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryLoading]);

  const featureFlagValues = useProductCardFeatureFlags();

  const categories = products?.categories || [];

  const productCards = useMemo(() => {
    const allProductCards = products?.cards || [];
    return filterProductCardsByFeatureFlags(allProductCards, featureFlagValues);
  }, [products?.cards, featureFlagValues]);
  const categoryActive = categories.find(
    (item) => formatSearchParams(fm({ id: item.titleKey })) === categorySlug
  );

  // Data for the banner
  const [propsBanner, setPropsBanner] = useState(DEFAULT_CATEGORY_FILTER);

  useEffect(() => {
    setPropsBanner(getBannerProps(categorySlug || DEFAULT_CATEGORY_FILTER));
  }, [categorySlug]);

  const bannerImageUrl = isMobile
    ? propsBanner?.bgrMobile
    : propsBanner?.bgrDesktop;

  // Get title page following the category filter
  const titlePage = categoryActive?.titleKey || 'products.title.all';

  // Scroll to top when component mounts
  useScrollTo(0, 0);

  return (
    <Styled.Root>
      <ProductsBreadcrumbs />
      <ProductCardsFilter items={categories} loading={loading} />
      <ProductBanners
        imageUrl={bannerImageUrl}
        title={propsBanner?.title}
        description={propsBanner?.description}
        logo={propsBanner?.logo}
        color={propsBanner?.color}
        loading={loading}
      />
      <Heading size="lg" weight="medium" color="primary" loading={loading} wrap="wrap">
        <FormattedMessage id={titlePage} />
      </Heading>
      <ProductCards items={productCards} loading={loading} />
    </Styled.Root>
  );
};

export default Products;
